import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { AxiosResponse } from 'axios'
import {
  IDayReportResponse,
  ILossParetoResponse,
  IMachineDayResponse,
  IShiftReviewResponse,
  ITimelineResponse,
  ITrendResponse,
  IWorkerDayResponse,
} from 'types/api/operationsAimApi.interface'
import { IPaginatedResponse } from 'types/api/general.interface'
import { IOpsAnalyticsSlice } from 'types/opsAnalyticsSlice.interface'
import { baseUrl } from 'utils/axios'
import { handleErrors } from 'utils/helpers'
import { USE_OPS_MOCKS } from 'pages/Operations/Shared/opsControl'
import {
  DEMO_LOSSES,
  DEMO_MACHINE_DAYS,
  DEMO_WORKER_DAYS,
  demoDayReports,
  demoMachineTimeline,
  demoShiftReview,
  demoTrend,
  demoWorkerTimeline,
} from 'pages/Operations/Shared/opsApiDemoAnalytics'

const initialState: IOpsAnalyticsSlice = {
  isLoading: false,
  loaded: false,
  machines: [],
  workers: [],
  shiftReview: null,
  losses: null,
  trends: null,
  trendsByMetric: {},
  dayReports: [],
  dayReportsTotalPages: 0,
  machineTimeline: null,
  workerTimeline: null,
}

export const getAnalyticsMachines = createAsyncThunk(
  'opsAnalytics/getAnalyticsMachines',
  async (params: any, thunkAPI) => {
    if (USE_OPS_MOCKS) return DEMO_MACHINE_DAYS
    try {
      const resp: AxiosResponse<IMachineDayResponse[]> = await baseUrl.get('analytics/machines', { params })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getAnalyticsWorkers = createAsyncThunk(
  'opsAnalytics/getAnalyticsWorkers',
  async (params: any, thunkAPI) => {
    if (USE_OPS_MOCKS) return DEMO_WORKER_DAYS
    try {
      const resp: AxiosResponse<IWorkerDayResponse[]> = await baseUrl.get('analytics/workers', { params })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getShiftReview = createAsyncThunk(
  'opsAnalytics/getShiftReview',
  async (params: any, thunkAPI) => {
    if (USE_OPS_MOCKS) return demoShiftReview(params)
    try {
      const resp: AxiosResponse<IShiftReviewResponse> = await baseUrl.get('analytics/shift-review', { params })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getAnalyticsLosses = createAsyncThunk(
  'opsAnalytics/getAnalyticsLosses',
  async (params: any, thunkAPI) => {
    if (USE_OPS_MOCKS) return DEMO_LOSSES
    try {
      const resp: AxiosResponse<ILossParetoResponse> = await baseUrl.get('analytics/losses', { params })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getAnalyticsTrends = createAsyncThunk(
  'opsAnalytics/getAnalyticsTrends',
  async (params: any, thunkAPI) => {
    if (USE_OPS_MOCKS) return { metric: params.metric as string, data: demoTrend(params.metric) }
    try {
      const resp: AxiosResponse<ITrendResponse> = await baseUrl.get('analytics/trends', { params })
      return { metric: params.metric as string, data: resp.data }
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getDayReports = createAsyncThunk(
  'opsAnalytics/getDayReports',
  async (params: any, thunkAPI) => {
    if (USE_OPS_MOCKS) return demoDayReports()
    try {
      const resp: AxiosResponse<IPaginatedResponse<IDayReportResponse>> = await baseUrl.get(
        'analytics/day-reports',
        { params },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getMachineTimeline = createAsyncThunk(
  'opsAnalytics/getMachineTimeline',
  async ({ id, params }: { id: number; params: any }, thunkAPI) => {
    if (USE_OPS_MOCKS) return demoMachineTimeline(id, params)
    try {
      const resp: AxiosResponse<ITimelineResponse> = await baseUrl.get(`analytics/machine/${id}/timeline`, {
        params,
      })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getWorkerTimeline = createAsyncThunk(
  'opsAnalytics/getWorkerTimeline',
  async ({ id, params }: { id: number; params: any }, thunkAPI) => {
    if (USE_OPS_MOCKS) return demoWorkerTimeline(id, params)
    try {
      const resp: AxiosResponse<ITimelineResponse> = await baseUrl.get(`analytics/worker/${id}/timeline`, {
        params,
      })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

const opsAnalyticsSlice = createSlice({
  name: 'opsAnalytics',
  initialState,
  reducers: {
    clearOpsAnalytics: () => initialState,
  },
  extraReducers: {
    [getAnalyticsMachines.pending.type]: (state) => {
      state.isLoading = true
    },
    [getAnalyticsMachines.fulfilled.type]: (state, { payload }: { payload: IMachineDayResponse[] }) => {
      state.isLoading = false
      state.loaded = true
      state.machines = payload || []
    },
    [getAnalyticsMachines.rejected.type]: (state, action) => {
      state.isLoading = false
      state.loaded = true
      handleErrors(action.payload)
    },
    [getAnalyticsWorkers.pending.type]: (state) => {
      state.isLoading = true
    },
    [getAnalyticsWorkers.fulfilled.type]: (state, { payload }: { payload: IWorkerDayResponse[] }) => {
      state.isLoading = false
      state.loaded = true
      state.workers = payload || []
    },
    [getAnalyticsWorkers.rejected.type]: (state, action) => {
      state.isLoading = false
      state.loaded = true
      handleErrors(action.payload)
    },
    [getShiftReview.pending.type]: (state) => {
      state.isLoading = true
    },
    [getShiftReview.fulfilled.type]: (state, { payload }: { payload: IShiftReviewResponse }) => {
      state.isLoading = false
      state.loaded = true
      state.shiftReview = payload
    },
    [getAnalyticsLosses.pending.type]: (state) => {
      state.isLoading = true
    },
    [getAnalyticsLosses.fulfilled.type]: (state, { payload }: { payload: ILossParetoResponse }) => {
      state.isLoading = false
      state.loaded = true
      state.losses = payload
    },
    [getAnalyticsTrends.fulfilled.type]: (
      state,
      { payload }: { payload: { metric: string; data: ITrendResponse } },
    ) => {
      state.isLoading = false
      state.loaded = true
      state.trends = payload.data
      state.trendsByMetric[payload.metric] = payload.data
    },
    [getDayReports.pending.type]: (state) => {
      state.isLoading = true
    },
    [getDayReports.fulfilled.type]: (
      state,
      { payload }: { payload: IPaginatedResponse<IDayReportResponse> },
    ) => {
      state.isLoading = false
      state.loaded = true
      state.dayReports = payload.data || []
      state.dayReportsTotalPages = payload.totalPages
    },
    [getMachineTimeline.fulfilled.type]: (state, { payload }: { payload: ITimelineResponse }) => {
      state.machineTimeline = payload
    },
    [getWorkerTimeline.fulfilled.type]: (state, { payload }: { payload: ITimelineResponse }) => {
      state.workerTimeline = payload
    },
    [getShiftReview.rejected.type]: (state, action) => {
      state.isLoading = false
      state.loaded = true
      handleErrors(action.payload)
    },
    [getAnalyticsLosses.rejected.type]: (state, action) => {
      state.isLoading = false
      state.loaded = true
      handleErrors(action.payload)
    },
    [getAnalyticsTrends.rejected.type]: (state, action) => {
      state.isLoading = false
      state.loaded = true
      handleErrors(action.payload)
    },
    [getDayReports.rejected.type]: (state, action) => {
      state.isLoading = false
      state.loaded = true
      handleErrors(action.payload)
    },
  },
})

export const { clearOpsAnalytics } = opsAnalyticsSlice.actions
export default opsAnalyticsSlice.reducer
